import { packingStateSchema, TRIP_TITLE_MAX_LENGTH, validateStateReferences, type PackingState } from './schema';
import { createDefaultDocuments } from './readiness';
import { createDepartureChecks } from './travel';
import { groupPackingItems, normalizeCompleteTravelState, setItemQuantity, setPresetQuantity } from './complete-travel';
import { ITEM_PRESETS } from '../content/catalog';
import { FREE_RIGHTS, type Rights } from './entitlement';

export function recordUsed(state: PackingState, usedItemIds: ReadonlySet<string>, reviewedAt = new Date().toISOString()): PackingState {
  const knownIds = new Set(state.items.map((item) => item.id));
  for (const id of usedItemIds) if (!knownIds.has(id)) throw new Error(`unknown item:${id}`);
  return packingStateSchema.parse({
    ...state,
    trip: { ...state.trip, status: 'archived', postTripReviewedAt: reviewedAt, updatedAt: reviewedAt },
    items: state.items.map((item) => ({ ...item, used: usedItemIds.has(item.id) })),
  });
}

function copyTitle(title: string): string {
  const suffix = ' 복사본';
  if (title.length + suffix.length <= TRIP_TITLE_MAX_LENGTH) return title + suffix;
  // The schema measures UTF-16 units; segment first so truncation never splits an emoji or combining sequence.
  if (typeof Intl.Segmenter !== 'function') return title;
  let prefix = '';
  for (const { segment } of new Intl.Segmenter('ko', { granularity: 'grapheme' }).segment(title)) {
    if (prefix.length + segment.length + suffix.length > TRIP_TITLE_MAX_LENGTH) break;
    prefix += segment;
  }
  return prefix ? prefix.trimEnd() + suffix : title;
}

export function cloneTrip(state: PackingState, nextTripId: string, createdAt = new Date().toISOString()): PackingState {
  if (!nextTripId.trim() || nextTripId === state.trip.id) throw new Error('clone trip id must be unique');
  state = packingStateSchema.parse(normalizeCompleteTravelState(state));
  const originalErrors = validateStateReferences(state);
  if (originalErrors.length) throw new Error('원본 여행의 연결을 먼저 확인해주세요. 원본 파일은 내 여행에서 받을 수 있어요.');
  const includedItems = state.items.filter((item) => item.used !== false);
  const bagIds = new Map(state.bags.map((bag, index) => [bag.id, `${nextTripId}:bag:${index + 1}`]));
  const containerIds = new Map(state.containers.map((container, index) => [container.id, `${nextTripId}:container:${index + 1}`]));
  const itemIds = new Map(includedItems.map((item, index) => [item.id, `${nextTripId}:item:${index + 1}`]));
  const participantIds = new Map((state.trip.participants ?? []).map((entry, index) => [entry.id, `${nextTripId}:person:${index + 1}`]));

  const clone: PackingState = {
    schemaVersion: 1,
    trip: {
      ...state.trip,
      id: nextTripId,
      title: copyTitle(state.trip.title),
      status: 'draft',
      bagIds: state.trip.bagIds.flatMap((id) => bagIds.get(id) ?? []),
      itemInstanceIds: state.trip.itemInstanceIds.flatMap((id) => itemIds.get(id) ?? []),
      extractionOrder: state.trip.extractionOrder.flatMap((id) => itemIds.get(id) ?? []),
      createdAt,
      updatedAt: createdAt,
      sourceTripId: state.trip.id,
      expenses: [], currencyRates: undefined, lastExportedAt:undefined,statusBeforeArchive:undefined,
      postTripReviewedAt: undefined,
      packingReview: undefined,
      forgottenItems: undefined,
      remoteShare: undefined,
      memories: [],
      shareSettings: { permission: 'view', includePacking: true, includePhotos: false, expiresInDays: 30 },
      participants: state.trip.participants?.map((person) => ({ ...person, id: participantIds.get(person.id)!, createdAt })),
      journey: state.trip.journey ? { destination: state.trip.journey.destination } : undefined,
      departureChecks: createDepartureChecks(false),
      scheduleEntries: (state.trip.scheduleEntries ?? []).map((entry, index) => ({ ...entry, id: `${nextTripId}:schedule:${index + 1}`, date: undefined, endDate: undefined, privateNote: undefined, participantIds: entry.participantIds?.map((id) => participantIds.get(id)!).filter(Boolean), completed: false })),
      travelDocuments: createDefaultDocuments(nextTripId),
    },
    bags: state.bags.map((bag) => ({
      ...bag,
      id: bagIds.get(bag.id)!,
      tripId: nextTripId,
      containerIds: bag.containerIds.flatMap((id) => containerIds.get(id) ?? []),
      confirmedAllowanceG: undefined,
      allowanceConfirmedAt: undefined,
      ruleCandidateId: undefined,
      allowanceGroupId: undefined,
      allowanceGroupLimitG: undefined,
    })),
    containers: state.containers.map((container) => ({
      ...container,
      id: containerIds.get(container.id)!,
      bagId: bagIds.get(container.bagId)!,
      parentContainerId: container.parentContainerId ? containerIds.get(container.parentContainerId) : undefined,
    })),
    items: includedItems.map((item) => ({
      ...item,
      id: itemIds.get(item.id)!,
      tripId: nextTripId,
      used: undefined,
      manuallyUnplaced: undefined,
      placementId: undefined,
      assignedBagId: item.assignedBagId ? bagIds.get(item.assignedBagId) : undefined,
      checklistStatus: 'planned',
    })),
    placements: [],
  };
  const parsed = packingStateSchema.parse(clone);
  const errors = validateStateReferences(parsed);
  if (errors.length) throw new Error(errors[0]);
  return parsed;
}

export type PackingReview = NonNullable<PackingState['trip']['packingReview']>;
export type ForgottenItems = NonNullable<PackingState['trip']['forgottenItems']>;

export function recordPackingReview(state: PackingState, review: PackingReview, forgottenItems: ForgottenItems, now = new Date().toISOString()): PackingState {
  const groups = groupPackingItems(state);
  if (review.length !== groups.length || new Set(review.map((row) => row.key)).size !== review.length) throw new Error('준비물별 사용 수량을 확인해주세요.');
  const usedIds = new Set<string>();
  for (const group of groups) {
    const row = review.find((value) => value.key === group.key);
    if (!row || !Number.isInteger(row.usedQuantity) || row.usedQuantity < 0 || row.usedQuantity > group.quantity) throw new Error(`${group.name} 실제 사용은 가져간 ${group.quantity}개 안에서 입력해주세요.`);
    for (const item of group.items.slice(0, row.usedQuantity)) usedIds.add(item.id);
  }
  return packingStateSchema.parse({ ...recordUsed(state, usedIds, now), trip: { ...state.trip, status: 'archived', updatedAt: now, postTripReviewedAt: now, packingReview: review, forgottenItems } });
}

export interface NextPackingRow { key: string; name: string; presetId?: string; weightG?: number; packed: number; used?: number; extra: number; quantity: number }

export function nextPackingSuggestions(state: PackingState, dismissed: string[] = []): NextPackingRow[] {
  const rows: NextPackingRow[] = groupPackingItems(state).map((group) => {
    const review = state.trip.packingReview?.find((entry) => entry.key === group.key);
    const used = review?.usedQuantity ?? (group.items.every((item) => typeof item.used === 'boolean') ? group.items.filter((item) => item.used === true).length : undefined);
    const extra = review?.extraQuantity ?? 0;
    return { key: group.key, name: group.name, presetId: group.presetId, packed: group.quantity, used, extra, quantity: (used ?? group.quantity) + extra };
  });
  for (const forgotten of state.trip.forgottenItems ?? []) {
    const key = forgotten.presetId ? `preset:${forgotten.presetId}` : `custom:${forgotten.name}`;
    const row = rows.find((entry) => entry.key === key);
    if (row) { row.extra += forgotten.quantity; row.quantity += forgotten.quantity; }
    else rows.push({ key, name: forgotten.name, presetId: forgotten.presetId, weightG: forgotten.weightG, packed: 0, used: 0, extra: forgotten.quantity, quantity: forgotten.quantity });
  }
  return rows.map((row)=>row.presetId&&dismissed.includes(row.presetId)?{...row,quantity:0}:row);
}

export function createNextTrip(state: PackingState, id: string, rows: NextPackingRow[], selectedBagIds: string[], now = new Date().toISOString(), rights: Rights = FREE_RIGHTS): PackingState {
  const expected = nextPackingSuggestions(state);
  if (rows.length !== expected.length || new Set(rows.map((row) => row.key)).size !== rows.length || rows.some((row) => !expected.some((value) => value.key === row.key))) throw new Error('미리보기 목록을 다시 확인해주세요.');
  if (rows.some((row) => !Number.isInteger(row.quantity) || row.quantity < 0 || row.quantity > rights.maxItemsPerTrip) || rows.reduce((sum, row) => sum + row.quantity, 0) > rights.maxItemsPerTrip) throw new Error(`새 여행 준비물을 ${rights.maxItemsPerTrip}개 이내로 조정해주세요. 원본은 그대로예요.`);
  if (!selectedBagIds.length || selectedBagIds.length > rights.maxBagsPerTrip || selectedBagIds.some((bagId) => !state.bags.some((bag) => bag.id === bagId))) throw new Error(`새 여행 가방을 1~${rights.maxBagsPerTrip}개 골라주세요. 원본은 그대로예요.`);
  // Reuse all old units before adjusting quantities: assignment/status preservation belongs to the quantity command.
  let next = cloneTrip({ ...state, items: state.items.map((item) => ({ ...item, used: undefined })) }, id, now);
  const selected = new Set(selectedBagIds.map((bagId) => next.bags[state.bags.findIndex((bag) => bag.id === bagId)]!.id));
  next = { ...next, bags: next.bags.filter((bag) => selected.has(bag.id)), containers: next.containers.filter((container) => selected.has(container.bagId)), items: next.items.map((item) => ({ ...item, assignedBagId: item.assignedBagId && selected.has(item.assignedBagId) ? item.assignedBagId : undefined })), trip: { ...next.trip, bagIds: next.trip.bagIds.filter((bagId) => selected.has(bagId)) } };
  // Reductions first allow an at-cap trip to replace unused items without a temporary overflow.
  for (const row of [...rows].sort((a, b) => (a.quantity - a.packed) - (b.quantity - b.packed))) {
    const source = expected.find((entry) => entry.key === row.key)!;
    const prototype = state.items.find((item) => source.presetId ? item.presetId === source.presetId : !item.presetId && item.name === source.name);
    const preset = ITEM_PRESETS.find((item) => item.id === source.presetId);
    if (prototype) next = setItemQuantity(next, prototype, row.quantity, prototype.ownerId, rights);
    else if (preset) next = setPresetQuantity(next, preset, row.quantity, undefined, rights);
    else {
      next = setItemQuantity(next, { name: source.name, packedDimensionsState: 'unknown', weightG: source.weightG, weightSource: source.weightG === undefined ? 'unknown' : 'measured', rotatable: false, tags: [] }, row.quantity, undefined, rights);
    }
  }
  const parsed = packingStateSchema.parse(next);
  const errors = validateStateReferences(parsed);
  if (errors.length) throw new Error(errors[0]);
  return parsed;
}

export function adoptSandboxTrip(state: PackingState, nextTripId: string, createdAt = new Date().toISOString()): PackingState {
  if (!nextTripId.trim() || nextTripId === state.trip.id) throw new Error('adopted trip id must be unique');
  const bagIds = new Map(state.bags.map((bag, index) => [bag.id, `${nextTripId}:bag-${index + 1}`]));
  const containerIds = new Map(state.containers.map((container, index) => [container.id, `${nextTripId}:container-${index + 1}`]));
  const itemIds = new Map(state.items.map((item, index) => [item.id, `${nextTripId}:item-${index + 1}`]));
  const placementIds = new Map(state.placements.map((placement, index) => [placement.id, `${nextTripId}:placement-${index + 1}`]));
  const participantIds = new Map((state.trip.participants ?? []).map((participant, index) => [participant.id, `${nextTripId}:participant-${index + 1}`]));
  const scheduleIds = new Map((state.trip.scheduleEntries ?? []).map((entry, index) => [entry.id, `${nextTripId}:schedule-${index + 1}`]));

  const adopted: PackingState = {
    schemaVersion: 1,
    trip: {
      ...state.trip,
      id: nextTripId,
      status: 'draft',
      bagIds: state.trip.bagIds.flatMap((id) => bagIds.get(id) ?? []),
      itemInstanceIds: state.trip.itemInstanceIds.flatMap((id) => itemIds.get(id) ?? []),
      extractionOrder: state.trip.extractionOrder.flatMap((id) => itemIds.get(id) ?? []),
      createdAt,
      updatedAt: createdAt,
      sourceTripId: undefined,
      postTripReviewedAt: undefined,
      journey: state.trip.journey ? {
        ...state.trip.journey,
        segments: state.trip.journey.segments?.map((segment, index) => ({ ...segment, id: `${nextTripId}:segment-${index + 1}` })),
      } : undefined,
      departureChecks: state.trip.departureChecks?.map((check, index) => ({ ...check, id: `${nextTripId}:departure-${index + 1}` })),
      scheduleEntries: state.trip.scheduleEntries?.map((entry) => ({
        ...entry,
        id: scheduleIds.get(entry.id)!,
        participantIds: entry.participantIds?.flatMap((id) => participantIds.get(id) ?? []),
      })),
      travelDocuments: state.trip.travelDocuments?.map((document, index) => ({ ...document, id: `${nextTripId}:document-${index + 1}` })),
      participants: state.trip.participants?.map((participant) => ({ ...participant, id: participantIds.get(participant.id)! })),
      memories: state.trip.memories?.map((memory, memoryIndex) => ({
        ...memory,
        id: `${nextTripId}:memory-${memoryIndex + 1}`,
        participantIds: memory.participantIds?.flatMap((id) => participantIds.get(id) ?? []),
        linkedScheduleEntryId: memory.linkedScheduleEntryId ? scheduleIds.get(memory.linkedScheduleEntryId) : undefined,
        photos: memory.photos.map((photo, photoIndex) => ({ ...photo, id: `${nextTripId}:photo-${memoryIndex + 1}-${photoIndex + 1}` })),
      })),
      remoteShare: undefined,
    },
    bags: state.bags.map((bag) => ({
      ...bag,
      id: bagIds.get(bag.id)!,
      tripId: nextTripId,
      containerIds: bag.containerIds.flatMap((id) => containerIds.get(id) ?? []),
    })),
    containers: state.containers.map((container) => ({
      ...container,
      id: containerIds.get(container.id)!,
      bagId: bagIds.get(container.bagId)!,
      parentContainerId: container.parentContainerId ? containerIds.get(container.parentContainerId) : undefined,
    })),
    items: state.items.map((item) => ({
      ...item,
      id: itemIds.get(item.id)!,
      tripId: nextTripId,
      assignedBagId: item.assignedBagId ? bagIds.get(item.assignedBagId) : undefined,
      placementId: item.placementId ? placementIds.get(item.placementId) : undefined,
    })),
    placements: state.placements.map((placement) => ({
      ...placement,
      id: placementIds.get(placement.id)!,
      itemInstanceId: itemIds.get(placement.itemInstanceId)!,
      containerId: containerIds.get(placement.containerId)!,
    })),
  };
  const parsed = packingStateSchema.parse(adopted);
  const errors = validateStateReferences(parsed);
  if (errors.length) throw new Error(errors[0]);
  return parsed;
}

export function activeTripCountAfterUpsert(existing: readonly PackingState[], incoming: readonly PackingState[]) {
  const byId = new Map(existing.map((state) => [state.trip.id, state]));
  for (const state of incoming) byId.set(state.trip.id, state);
  return [...byId.values()].filter((state) => state.trip.status !== 'archived').length;
}
